<template>
  <AiEducationLayout>
    <div class="ai-edu-admin-page">
      <ContentWrap title="直播场次">
        <el-form :inline="true" :model="query" class="-mb-15px">
          <el-form-item label="学校">
            <el-select
              v-model="query.schoolId"
              class="!w-200px"
              clearable
              filterable
              @change="onSchoolChange"
            >
              <el-option v-for="s in schools" :key="s.id" :label="s.name" :value="s.id!" />
            </el-select>
          </el-form-item>
          <el-form-item label="教学班">
            <el-select
              v-model="query.teachingClassId"
              class="!w-220px"
              clearable
              filterable
              @change="handleQuery"
            >
              <el-option v-for="c in teachingClasses" :key="c.id" :label="c.name" :value="c.id!" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button @click="handleQuery"><Icon icon="ep:search" />搜索</el-button>
            <el-button
              v-hasPermi="['ai-education:teach-session:write']"
              :disabled="!query.teachingClassId"
              plain
              type="primary"
              @click="openDialog()"
            >
              <Icon icon="ep:plus" />新增场次
            </el-button>
          </el-form-item>
        </el-form>
      </ContentWrap>
      <ContentWrap>
        <el-table v-loading="loading" :data="list" border>
          <el-table-column prop="title" label="场次标题" min-width="160" />
          <el-table-column prop="teachingClassName" label="教学班" min-width="140" />
          <el-table-column label="直播间" min-width="140">
            <template #default="{ row }">{{ roomName(row.liveRoomId) }}</template>
          </el-table-column>
          <el-table-column prop="scheduledStartTime" label="计划开始" width="170" />
          <el-table-column prop="scheduledEndTime" label="计划结束" width="170" />
          <el-table-column label="状态" width="100">
            <template #default="{ row }">
              <dict-tag :type="DICT_TYPE.AI_EDU_TEACH_SESSION_STATUS" :value="row.status" />
            </template>
          </el-table-column>
          <el-table-column prop="createTime" label="创建时间" width="170" />
          <el-table-column label="操作" width="160" fixed="right">
            <template #default="{ row }">
              <el-button
                v-hasPermi="['ai-education:teach-session:write']"
                link
                type="primary"
                @click="openDialog(row)"
                >编辑</el-button
              >
              <el-button
                v-hasPermi="['ai-education:teach-session:write']"
                link
                type="danger"
                @click="handleDelete(row.id)"
                >删除</el-button
              >
            </template>
          </el-table-column>
        </el-table>
      </ContentWrap>

      <Dialog v-model="dialogVisible" :title="form.id ? '编辑场次' : '新增场次'" width="560px">
        <el-form ref="formRef" :model="form" :rules="rules" label-width="110px">
          <el-form-item label="教学班" prop="teachingClassId">
            <el-select v-model="form.teachingClassId" class="w-100%" filterable>
              <el-option v-for="c in teachingClasses" :key="c.id" :label="c.name" :value="c.id!" />
            </el-select>
          </el-form-item>
          <el-form-item label="标题" prop="title"><el-input v-model="form.title" /></el-form-item>
          <el-form-item label="直播间" prop="liveRoomId">
            <el-select v-model="form.liveRoomId" class="w-100%" filterable>
              <el-option v-for="r in rooms" :key="r.id" :label="r.title" :value="r.id!" />
            </el-select>
          </el-form-item>
          <el-form-item label="计划开始">
            <el-date-picker
              v-model="form.scheduledStartTime"
              class="!w-100%"
              type="datetime"
              value-format="YYYY-MM-DD HH:mm:ss"
            />
          </el-form-item>
          <el-form-item label="计划结束">
            <el-date-picker
              v-model="form.scheduledEndTime"
              class="!w-100%"
              type="datetime"
              value-format="YYYY-MM-DD HH:mm:ss"
            />
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="form.status" class="w-100%">
              <el-option
                v-for="d in getIntDictOptions(DICT_TYPE.AI_EDU_TEACH_SESSION_STATUS)"
                :key="d.value"
                :label="d.label"
                :value="d.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="备注"><el-input v-model="form.remark" type="textarea" /></el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button :loading="saving" type="primary" @click="submit">保存</el-button>
        </template>
      </Dialog>
    </div>
  </AiEducationLayout>
</template>

<script lang="ts" setup>
import * as AiEducationApi from '@/api/aiEducation'
import * as UniversityApi from '@/api/aiEducation/university'
import AiEducationLayout from '../../layout/AiEducationLayout.vue'
import { getRoomPage } from '@/api/live/room'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import type { FormInstance, FormRules } from 'element-plus'

defineOptions({ name: 'AiEduTeachSession' })

const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const list = ref<UniversityApi.TeachSessionVO[]>([])
const schools = ref<AiEducationApi.SchoolVO[]>([])
const teachingClasses = ref<AiEducationApi.TeachingClassVO[]>([])
const rooms = ref<Array<{ id?: number; title: string }>>([])
const query = reactive<{ schoolId?: number; teachingClassId?: number }>({})
const dialogVisible = ref(false)
const formRef = ref<FormInstance>()
const form = reactive<Partial<UniversityApi.TeachSessionVO>>({})
const rules: FormRules = {
  teachingClassId: [{ required: true, message: '请选择教学班', trigger: 'change' }],
  title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
  liveRoomId: [{ required: true, message: '请选择直播间', trigger: 'change' }]
}

const roomName = (id?: number) => rooms.value.find((r) => r.id === id)?.title || (id ? `#${id}` : '-')

const onSchoolChange = async () => {
  query.teachingClassId = undefined
  teachingClasses.value = query.schoolId
    ? (await AiEducationApi.getTeachingClassList({ schoolId: query.schoolId })) || []
    : []
  list.value = []
}
const load = async () => {
  if (!query.teachingClassId) {
    list.value = []
    return
  }
  loading.value = true
  try {
    list.value = (await UniversityApi.getTeachSessionList(query.teachingClassId)) || []
  } finally {
    loading.value = false
  }
}
const handleQuery = () => load()
const openDialog = (row?: UniversityApi.TeachSessionVO) => {
  Object.assign(form, {
    id: row?.id,
    teachingClassId: row?.teachingClassId ?? query.teachingClassId,
    title: row?.title || '',
    liveRoomId: row?.liveRoomId,
    scheduledStartTime: row?.scheduledStartTime,
    scheduledEndTime: row?.scheduledEndTime,
    status: row?.status ?? 10,
    remark: row?.remark || ''
  })
  dialogVisible.value = true
}
const submit = async () => {
  await formRef.value?.validate()
  saving.value = true
  try {
    await UniversityApi.saveTeachSession(form)
    message.success('保存成功')
    dialogVisible.value = false
    await load()
  } finally {
    saving.value = false
  }
}
const handleDelete = async (id?: number) => {
  if (!id) return
  await message.delConfirm()
  await UniversityApi.deleteTeachSession(id)
  message.success('删除成功')
  await load()
}

onMounted(async () => {
  schools.value = (await AiEducationApi.getSchoolList()) || []
  const page: any = await getRoomPage({ pageNo: 1, pageSize: 100 })
  rooms.value = page?.list || []
})
</script>
